import React, { type DragEvent, type ImgHTMLAttributes, useEffect, useRef, useMemo } from 'react'
import cl from 'classnames';
import { getCDNImageUrl, getSrcset } from '@helper/image';
import css from './Image.module.scss';

type SourceType = {
    width?: number
    height?: number
    media: string
} | {
    width: number
    height: number
    media?: string
}

export type ResponsiveImageProps = ImgHTMLAttributes<HTMLImageElement> & {
    isDraggable?: boolean
    sources?: SourceType[]
}

function ResponsiveImage({ src, sources, isDraggable = false, className, ...rest }: ResponsiveImageProps ) {
    if(!src) return null
    const onDragStart = isDraggable ? undefined : (e: DragEvent<HTMLImageElement>)=> e.preventDefault();
    if(!sources || !sources.length) {
        return <img src={src} className={className} onDragStart={onDragStart} {...rest} />
    }
    const ref = useRef<HTMLImageElement>(null);
    useEffect(() => {
        if(ref.current) {
            const clearStyles = () => {
                if(!ref.current) return;
                ref.current.classList.remove(css.preloading);
            }

            const image = new Image();
            image.src = ref.current.currentSrc;

            // Becase of the cache, the image may be loaded before the event listener is attached
            image.addEventListener('load', clearStyles);
            ref.current.addEventListener('load', clearStyles);
            return () => {
                image.removeEventListener('load', clearStyles);
                ref.current?.removeEventListener('load', clearStyles);
            }
        }
    }, [])
    const maxWidth = useMemo(() => sources.map(s => s.width).filter(Boolean).sort((a, b) => a ?? 0 - (b ?? 0))[0] ?? 1920, [sources]);
    const maxHeight = useMemo(() => sources.map(s => s.height).filter(Boolean).sort((a, b) => a ?? 0 - (b ?? 0))[0] ?? 1080, [sources]);
    const fallbackSrc = getCDNImageUrl(src, { width: maxWidth, height: maxHeight, autorotate: 'true'})
    return (<picture>
        {sources.map(({ width, height, media}) => {
            const _src = getCDNImageUrl(src, { width, height, format: 'webp', autorotate: 'true'})
            return <source key={media || JSON.stringify({ width, height })} srcSet={getSrcset(_src)} media={media} type="image/webp"/>
        })}
        
        <img
            {...rest}
            ref={ref}
            src={fallbackSrc}
            onDragStart={onDragStart}
            className={cl(className, css.preloading)}
            />
    </picture>)
}

export default ResponsiveImage;